import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import {
  LayoutDashboard, Users, GraduationCap, Calendar, Clock,
  ClipboardCheck, MessageSquare, DollarSign, FileText,
  Folder, FileQuestion, BookOpen, BarChart3, Award, Database,
  Bell, Settings, ChevronLeft, ChevronRight, Home, HardDrive,
  Menu, X, ScrollText, Sparkles, Megaphone,
} from 'lucide-react';

const Sidebar = () => {
  const { language } = useLanguage();
  const { user, getAuthHeaders } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const role = user?.role;
    if (!user || !['school_admin', 'school'].includes(role) && role !== undefined) {
      // We only fetch for school accounts; schools don't always have an explicit role
    }
    if (!user) return;
    const tick = async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/parent-leads/unread-summary`,
          { headers: getAuthHeaders() }
        );
        if (!r.ok) return;
        const d = await r.json();
        setUnread((d.unread_messages || 0) + (d.pending_enrollments || 0));
      } catch (_e) { /* noop */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const t = (key) => {
    const translations = {
      fr: {
        dashboard: 'Tableau de bord',
        admissions: 'Bureau des Admissions',
        students: 'Élèves',
        teachers: 'Corps enseignant',
        structure: 'Structure',
        calendar: 'Calendrier',
        attendance: 'Présences',
        timetable: 'Emploi du temps',
        communication: 'Communications internes',
        fees: 'Frais de scolarité',
        gradebook: 'Notes & Évaluations',
        homework: 'Devoirs',
        courses: 'Cours',
        quizzes: 'Évaluations en ligne',
        reportCards: 'Bulletins',
        analytics: 'Statistiques',
        ubo: 'Certification UBO',
        export: 'Export institutionnel',
        notifications: 'Notifications',
        offline: 'Contenu hors ligne',
        settings: 'Paramètres',
        operations: 'Vie scolaire',
        academics: 'Pédagogie',
        advanced: 'Direction'
      },
      en: {
        dashboard: 'Dashboard',
        admissions: 'Admissions Office',
        students: 'Students',
        teachers: 'Faculty',
        structure: 'Structure',
        calendar: 'Calendar',
        attendance: 'Attendance',
        timetable: 'Timetable',
        communication: 'Internal Communications',
        fees: 'Tuition Fees',
        gradebook: 'Grades & Assessments',
        homework: 'Homework',
        courses: 'Courses',
        quizzes: 'Online Assessments',
        reportCards: 'Report Cards',
        analytics: 'Statistics',
        ubo: 'UBO Certification',
        export: 'Institutional Export',
        notifications: 'Notifications',
        offline: 'Offline Content',
        settings: 'Settings',
        operations: 'School Life',
        academics: 'Academics',
        advanced: 'Administration'
      }
    };
    return translations[language]?.[key] || translations.fr[key] || key;
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const role = user?.role;
  const isAdminRole = role === 'super_admin' || role === 'admin';
  const marketingPath = isAdminRole ? '/admin/familles-compatibles' : '/school/familles-compatibles';

  const menuSections = [
    {
      title: null, // Main dashboard
      items: [
        { path: '/dashboard', icon: LayoutDashboard, label: t('dashboard'), color: 'text-blue-500' },
        { path: marketingPath, icon: Megaphone, label: 'UBO Marketing', color: 'text-fuchsia-500', marketing: true },
        { path: '/admissions', icon: ScrollText, label: t('admissions'), color: 'text-amber-500', badge: unread || null },
        { path: '/nucleus-capital', icon: Award, label: language === 'fr' ? 'Nucleus Capital' : 'Nucleus Capital', color: 'text-amber-500', highlight: true },
        { path: '/nucleus-pay', icon: DollarSign, label: language === 'fr' ? 'Nucleus Pay' : 'Nucleus Pay', color: 'text-emerald-500' },
        { path: '/community', icon: MessageSquare, label: language === 'fr' ? 'Conseil UB' : 'UB Council', color: 'text-rose-500' },
        { path: '/world-school', icon: Sparkles, label: language === 'fr' ? 'World School (curriculum)' : 'World School (curriculum)', color: 'text-purple-500' },
      ]
    },
    {
      title: t('operations'),
      items: [
        { path: '/school/attendance', icon: ClipboardCheck, label: t('attendance'), color: 'text-green-500' },
        { path: '/school/timetable', icon: Clock, label: t('timetable'), color: 'text-blue-500' },
        { path: '/school/communication', icon: MessageSquare, label: t('communication'), color: 'text-purple-500' },
        { path: '/school/fees', icon: DollarSign, label: t('fees'), color: 'text-amber-500' }
      ]
    },
    {
      title: t('academics'),
      items: [
        { path: '/school/gradebook', icon: GraduationCap, label: t('gradebook'), color: 'text-indigo-500' },
        { path: '/school/homework', icon: FileText, label: t('homework'), color: 'text-pink-500' },
        { path: '/school/courses', icon: Folder, label: t('courses'), color: 'text-teal-500' },
        { path: '/school/quizzes', icon: FileQuestion, label: t('quizzes'), color: 'text-violet-500' },
        { path: '/school/report-cards', icon: BookOpen, label: t('reportCards'), color: 'text-orange-500' }
      ]
    },
    {
      title: t('advanced'),
      items: [
        { path: '/school/analytics', icon: BarChart3, label: t('analytics'), color: 'text-cyan-500' },
        { path: '/ubo', icon: Award, label: t('ubo'), color: 'text-yellow-500' },
        { path: '/offline-content', icon: HardDrive, label: t('offline'), color: 'text-slate-500' }
      ]
    },
    {
      title: t('settings'),
      items: [
        { path: '/settings/export', icon: Database, label: t('export'), color: 'text-emerald-500' },
        { path: '/settings/notifications', icon: Bell, label: t('notifications'), color: 'text-red-500' }
      ]
    }
  ];

  // Filter menu based on user role
  const getFilteredMenu = () => {
    const role = user?.role;
    if (role === 'student') {
      return [
        {
          title: null,
          items: [
            { path: '/student/dashboard', icon: LayoutDashboard, label: t('dashboard'), color: 'text-blue-500' }
          ]
        },
        {
          title: t('academics'),
          items: [
            { path: '/student/quizzes', icon: FileQuestion, label: t('quizzes'), color: 'text-violet-500' },
            { path: '/student/homework', icon: FileText, label: t('homework'), color: 'text-pink-500' },
            { path: '/student/grades', icon: GraduationCap, label: t('gradebook'), color: 'text-indigo-500' }
          ]
        },
        {
          title: t('advanced'),
          items: [
            { path: '/offline-content', icon: HardDrive, label: t('offline'), color: 'text-slate-500' }
          ]
        }
      ];
    }
    if (role === 'parent') {
      return [
        {
          title: null,
          items: [
            { path: '/parent/dashboard', icon: LayoutDashboard, label: t('dashboard'), color: 'text-blue-500' }
          ]
        },
        {
          title: t('academics'),
          items: [
            { path: '/parent/progress', icon: BarChart3, label: t('analytics'), color: 'text-cyan-500' }
          ]
        },
        {
          title: t('settings'),
          items: [
            { path: '/settings/notifications', icon: Bell, label: t('notifications'), color: 'text-red-500' }
          ]
        }
      ];
    }
    if (role === 'teacher') {
      return [
        {
          title: null,
          items: [
            { path: '/teacher/dashboard', icon: LayoutDashboard, label: t('dashboard'), color: 'text-blue-500' }
          ]
        },
        {
          title: t('operations'),
          items: [
            { path: '/school/attendance', icon: ClipboardCheck, label: t('attendance'), color: 'text-green-500' },
            { path: '/school/communication', icon: MessageSquare, label: t('communication'), color: 'text-purple-500' }
          ]
        },
        {
          title: t('academics'),
          items: [
            { path: '/school/gradebook', icon: GraduationCap, label: t('gradebook'), color: 'text-indigo-500' },
            { path: '/school/homework', icon: FileText, label: t('homework'), color: 'text-pink-500' },
            { path: '/school/courses', icon: Folder, label: t('courses'), color: 'text-teal-500' },
            { path: '/school/quizzes', icon: FileQuestion, label: t('quizzes'), color: 'text-violet-500' }
          ]
        },
        {
          title: t('advanced'),
          items: [
            { path: '/teacher/analytics', icon: BarChart3, label: t('analytics'), color: 'text-cyan-500' },
            { path: '/ubo', icon: Award, label: t('ubo'), color: 'text-yellow-500' }
          ]
        }
      ];
    }
    // Admin or super_admin - full menu
    return menuSections;
  };

  const filteredMenu = getFilteredMenu();

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo-ubo-white.png" alt="Nucleus" className="h-8" />
          {!collapsed && <span className="font-bold text-white">NUCLEUS</span>}
        </Link>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setCollapsed(!collapsed)}
          className="text-slate-400 hover:text-white hidden md:flex"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* Menu */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {filteredMenu.map((section, sIdx) => (
          <div key={sIdx} className="mb-4">
            {section.title && !collapsed && (
              <p className="px-3 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {section.title}
              </p>
            )}
            <div className="space-y-1">
              {section.items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                    isActive(item.path)
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                      : item.marketing
                        ? "text-white bg-fuchsia-600/15 ring-1 ring-fuchsia-500/40 hover:bg-fuchsia-600/30"
                        : "text-slate-300 hover:bg-slate-700/50 hover:text-white"
                  )}
                  data-testid={`sidebar-${item.path.split('/').pop()}`}
                >
                  <item.icon className={cn("h-5 w-5 flex-shrink-0", isActive(item.path) ? "text-white" : item.color)} />
                  {!collapsed && (
                    <span className="font-medium truncate flex-1">{item.label}</span>
                  )}
                  {!collapsed && item.badge ? (
                    <span
                      data-testid={`sidebar-badge-${item.path.split('/').pop()}`}
                      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold animate-pulse"
                    >
                      {item.badge}
                    </span>
                  ) : null}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User info */}
      {user && !collapsed && (
        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
              {user.first_name?.[0] || user.email?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-xs text-slate-400 truncate capitalize">
                {user.role === 'super_admin' ? 'Admin UBO' : user.role}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-slate-800 rounded-lg text-white shadow-lg"
        data-testid="mobile-menu-toggle"
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <aside 
        className={cn(
          "hidden md:flex flex-col bg-slate-800 border-r border-slate-700 transition-all duration-300",
          collapsed ? "w-20" : "w-64"
        )}
      >
        <SidebarContent />
      </aside>

      {/* Sidebar - Mobile */}
      <aside 
        className={cn(
          "md:hidden fixed inset-y-0 left-0 z-50 w-64 bg-slate-800 transform transition-transform duration-300",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <SidebarContent />
      </aside>
    </>
  );
};

export default Sidebar;
